import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonPageHeader,
  OktonPanel,
  useOktonAuth,
  useOktonPlatform,
  OktonLoadingState,
  OktonEmptyState,
  OktonInput,
  OktonSelect,
  OktonTextarea,
} from "@okton/platform-ui";



interface SystemAdminData {
  id: string;
  name: string;
  description?: string;
  category?: string;
  active: boolean;
  environment: {
    baseUrl?: string;
    redirectUri?: string;
    postLoginUrl?: string;
    postLogoutUrl?: string;
  };
  auth: {
    mode?: string;
    serverUrl?: string;
    realm?: string;
    clientId?: string;
    pkce: boolean;
    companyContext?: string;
    forgotPassword: boolean;
    createAccount: boolean;
    autoLogin: boolean;
  };
  features: {
    billing: boolean;
    profile: boolean;
    branchSwitching: boolean;
    notifications: boolean;
  };
  defaultAllowed: boolean | null;
}

export const SystemsPage: React.FC = () => {
  const { applications, isLoading, refreshApplications } = useOktonPlatform();
  const { accessToken } = useOktonAuth();
  const modal = useDisclosure();
  const [editing, setEditing] = React.useState<SystemAdminData | null>(null);
  const [loadingEditor, setLoadingEditor] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  async function editSystem(id: string): Promise<void> {
    if (!accessToken) return;
    setLoadingEditor(true);
    setError("");
    modal.onOpen();
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/applications/${encodeURIComponent(id)}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível carregar o sistema.",
        );
      setEditing(payload.data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar sistema.",
      );
    } finally {
      setLoadingEditor(false);
    }
  }

  async function saveSystem(): Promise<void> {
    if (!accessToken || !editing) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/applications/${encodeURIComponent(editing.id)}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(editing),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível salvar o sistema.",
        );
      setEditing(payload.data);
      await refreshApplications();
      modal.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar sistema.");
    } finally {
      setSaving(false);
    }
  }

  function patch(key: string, value: any): void {
    setEditing((current) => (current ? { ...current, [key]: value } : current));
  }

  function patchEnvironment(key: string, value: string): void {
    setEditing((current) =>
      current
        ? {
            ...current,
            environment: { ...current.environment, [key]: value },
          }
        : current,
    );
  }

  function patchAuth(key: string, value: string | boolean): void {
    setEditing((current) =>
      current
        ? {
            ...current,
            auth: { ...current.auth, [key]: value },
          }
        : current,
    );
  }

  function patchFeature(key: string, value: boolean): void {
    setEditing((current) =>
      current
        ? {
            ...current,
            features: { ...current.features, [key]: value },
          }
        : current,
    );
  }

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Sistemas"
        subtitle="Aplicações disponíveis e configurações centrais da plataforma."
        actions={
          <Badge colorScheme="blue" px="3" py="1" rounded="full">
            {applications.length} sistema{applications.length === 1 ? "" : "s"}
          </Badge>
        }
      />

      {isLoading ? (
        <OktonLoadingState title="Carregando sistemas..." minH="180px" />
      ) : applications.length === 0 ? (
        <OktonEmptyState
          title="Nenhum sistema disponível"
          description="Não há sistemas liberados para esta empresa/filial."
        />
      ) : (
        <SimpleGrid columns={[1, 2, 3]} spacing="4">
          {applications.map((application) => (
            <OktonPanel
              key={application.id}
              title={application.name}
              subtitle={application.id}
              headerActions={
                <Badge
                  colorScheme={application.active === false ? "gray" : "green"}
                >
                  {application.active === false ? "Inativo" : "Ativo"}
                </Badge>
              }
            >

              {application.description && (
                <Text fontSize="sm" color="gray.500" mb="4">
                  {application.description}
                </Text>
              )}
              {application.category && (
                <Text fontSize="xs" color="gray.500" mb="3">
                  Categoria: {application.category}
                </Text>
              )}

              <Flex gap="3" align="center">
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => editSystem(application.id)}
                >
                  Editar
                </Button>
                <Link
                  href={application.url}
                  isExternal
                  color="blue.500"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Abrir sistema
                </Link>
              </Flex>
            </OktonPanel>
          ))}
        </SimpleGrid>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar sistema {editing?.id || ""}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {error && (
              <Text color="red.500" mb="4">
                {error}
              </Text>
            )}
            {loadingEditor || !editing ? (
              <OktonLoadingState
                title="Carregando configuração do sistema..."
                minH="160px"
              />
            ) : (
              <Stack spacing="6">
                <Box>
                  <Heading size="sm" mb="3">
                    Identificação
                  </Heading>
                  <SimpleGrid columns={[1, 2]} spacing="4">
                    <OktonInput
                      name="system-name"
                      label="Nome"
                      required
                      value={editing.name}
                      onChange={(e) => patch("name", e.target.value)}
                    />
                    <OktonInput
                      name="system-category"
                      label="Categoria"
                      value={editing.category || ""}
                      onChange={(e) => patch("category", e.target.value)}
                    />
                  </SimpleGrid>
                  <Box mt="4">
                    <OktonTextarea
                      name="system-description"
                      label="Descrição"
                      value={editing.description || ""}
                      onChange={(e) => patch("description", e.target.value)}
                    />
                  </Box>
                </Box>

                <Box>
                  <Heading size="sm" mb="3">
                    Ambiente de produção
                  </Heading>
                  <SimpleGrid columns={[1, 2]} spacing="4">
                    <OktonInput
                      name="system-base-url"
                      label="URL base"
                      required
                      value={editing.environment.baseUrl || ""}
                      onChange={(e) =>
                        patchEnvironment("baseUrl", e.target.value)
                      }
                    />
                    <OktonInput
                      name="system-redirect-uri"
                      label="Redirect URI"
                      value={editing.environment.redirectUri || ""}
                      onChange={(e) =>
                        patchEnvironment("redirectUri", e.target.value)
                      }
                    />
                    <OktonInput
                      name="system-post-login-url"
                      label="Pós-login"
                      value={editing.environment.postLoginUrl || ""}
                      onChange={(e) =>
                        patchEnvironment("postLoginUrl", e.target.value)
                      }
                    />
                    <OktonInput
                      name="system-post-logout-url"
                      label="Pós-logout"
                      value={editing.environment.postLogoutUrl || ""}
                      onChange={(e) =>
                        patchEnvironment("postLogoutUrl", e.target.value)
                      }
                    />
                  </SimpleGrid>
                </Box>

                <Box>
                  <Heading size="sm" mb="3">
                    Autenticação
                  </Heading>
                  <SimpleGrid columns={[1, 2]} spacing="4">
                    <OktonSelect
                      name="system-auth-mode"
                      label="Modo"
                      value={editing.auth.mode || ""}
                      onChange={(e) => patchAuth("mode", e.target.value)}
                    >
                      <option value="keycloak">keycloak</option>
                      <option value="keycloak-bff">keycloak-bff</option>
                    </OktonSelect>
                    <OktonInput
                      name="system-auth-server"
                      label="Servidor"
                      value={editing.auth.serverUrl || ""}
                      onChange={(e) => patchAuth("serverUrl", e.target.value)}
                    />
                    <OktonInput
                      name="system-auth-realm"
                      label="Realm"
                      value={editing.auth.realm || ""}
                      onChange={(e) => patchAuth("realm", e.target.value)}
                    />
                    <OktonInput
                      name="system-auth-client-id"
                      label="Client ID"
                      value={editing.auth.clientId || ""}
                      onChange={(e) => patchAuth("clientId", e.target.value)}
                    />
                    <OktonSelect
                      name="system-company-context"
                      label="Contexto empresarial"
                      value={editing.auth.companyContext || "none"}
                      onChange={(e) =>
                        patchAuth("companyContext", e.target.value)
                      }
                    >
                      <option value="none">none</option>
                      <option value="required">required</option>
                      <option value="optional">optional</option>
                    </OktonSelect>
                  </SimpleGrid>
                  <Flex wrap="wrap" gap="5" mt="4">
                    <Checkbox
                      isChecked={editing.auth.pkce}
                      onChange={(e) => patchAuth("pkce", e.target.checked)}
                    >
                      PKCE
                    </Checkbox>
                    <Checkbox
                      isChecked={editing.auth.autoLogin}
                      onChange={(e) => patchAuth("autoLogin", e.target.checked)}
                    >
                      Auto login
                    </Checkbox>
                    <Checkbox
                      isChecked={editing.auth.forgotPassword}
                      onChange={(e) =>
                        patchAuth("forgotPassword", e.target.checked)
                      }
                    >
                      Recuperar senha
                    </Checkbox>
                    <Checkbox
                      isChecked={editing.auth.createAccount}
                      onChange={(e) =>
                        patchAuth("createAccount", e.target.checked)
                      }
                    >
                      Criar conta
                    </Checkbox>
                  </Flex>
                </Box>

                <Box>
                  <Heading size="sm" mb="3">
                    Capacidades e acesso
                  </Heading>
                  <Flex wrap="wrap" gap="5">
                    <Checkbox
                      isChecked={editing.features.profile}
                      onChange={(e) =>
                        patchFeature("profile", e.target.checked)
                      }
                    >
                      Perfil
                    </Checkbox>
                    <Checkbox
                      isChecked={editing.features.billing}
                      onChange={(e) =>
                        patchFeature("billing", e.target.checked)
                      }
                    >
                      Faturamento
                    </Checkbox>
                    <Checkbox
                      isChecked={editing.features.branchSwitching}
                      onChange={(e) =>
                        patchFeature("branchSwitching", e.target.checked)
                      }
                    >
                      Troca de filial
                    </Checkbox>
                    <Checkbox
                      isChecked={editing.features.notifications}
                      onChange={(e) =>
                        patchFeature("notifications", e.target.checked)
                      }
                    >
                      Notificações
                    </Checkbox>
                    <Checkbox
                      isChecked={editing.defaultAllowed === true}
                      onChange={(e) =>
                        patch("defaultAllowed", e.target.checked)
                      }
                    >
                      Acesso padrão liberado
                    </Checkbox>
                  </Flex>
                </Box>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={saveSystem}
              isLoading={saving}
              isDisabled={!editing || loadingEditor}
            >
              Salvar alterações
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
